<template>
  <AdminLayout>
    <div class="dashboard-container">
      <div class="result-card d-none">
        <canvas ref="resultCanvas" :width="canvasWidth" :height="canvasHeight" style="display: none"></canvas>
      </div>

      <div class="row">
        <div class="row col-lg-9">
          <!-- Header Section -->
          <div class="row">
            <div class="col-12 mb-4">
              <div class="control-panel">
                <label for="eventSelect" class="form-label">Select Event:</label>
                <div class="controls-row">
                  <!-- Event select dropdown -->
                  <select id="eventSelect" class="form-select event-select" v-model="eventId">
                    <option v-for="event in events" :key="event.id" :value="event.id">
                      🏆 {{ event.name }}
                    </option>
                  </select>

                  <!-- Action buttons -->
                  <div class="btn-group action-buttons">
                    <button @click.stop.prevent="setAsActive" class="btn btn-active d-flex align-items-center">
                      <i class="bi bi-check-circle me-2"></i> Set as Active
                    </button>
                    <button @click.stop.prevent="toggleStatus" :class="{
                      'btn-closed': currentEvent.isOpen,
                      'btn-open': !currentEvent.isOpen
                    }" class="btn status-btn d-flex align-items-center">
                      <i :class="currentEvent.isOpen ? 'bi bi-lock' : 'bi bi-unlock'" class="me-2"></i>
                      {{ currentEvent.isOpen ? 'Close' : 'Open' }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Print Section -->
          <div class="d-none d-print-block text-center print-header">
            <h1>{{ currentEvent?.name }}</h1>
            <h3>Score Sheet</h3>
          </div>

          <!-- Overall Scores Table -->
          <div class="table-section">
            <div class="table-header d-print-none">
              <button class="btn btn-print" @click="printWinners">
                <i class="bi bi-printer me-2"></i> Print Winners
              </button>
            </div>

            <div class="table-wrapper">
              <table class="table scores-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Contestant Name</th>
                    <th>Contestant Agency</th>
                    <th>Total Score</th>
                    <th>Rank</th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="scoreCard in overAllScoreCards" :key="scoreCard.contestantId"
                    :class="{ 'tied-row': isTied(scoreCard.rank) }">
                    <td class="contestant-number">{{ scoreCard.userPK }}</td>
                    <td class="contestant-name">{{ scoreCard.contestantName }}</td>
                    <td class="contestant-agency">{{ scoreCard.contestantAgency }}</td>
                    <td class="total-score">{{ scoreCard.averageScore ? (scoreCard.averageScore).toFixed(2) : '0.00' }}
                    </td>
                    <td class="rank" :class="getRankClass(scoreCard.rank)">{{ scoreCard.rank }}</td>
                  </tr>

                </tbody>

              </table>
            </div>
            <div v-if="!currentEvent.isSpecialEvent && scoreCards.length > 0" class="d-flex justify-content-end w-100 mt-2">
              <button @click="addToMagicThreeScores" class="btn btn-danger">Add To Magic 3 Scores</button>
            </div>
          </div>

          <!-- Judges Tabs and Scores -->
          <div class="judges-section">
            <ul class="judges-tabs d-print-none">
              <li class="judge-tab" v-for="judge in judges" :key="judge.id" :title="'Select ' + judge.name">
                <button class="tab-button" :class="{ active: selectedJudgeId === judge.id }"
                  @click="selectJudge(judge)">
                  {{ judge.name }}
                </button>
              </li>
            </ul>

            <div class="detailed-scores">
              <div class="score-header d-print-none">
                <h3 class="judge-title">{{ selectedJudge?.description?.toUpperCase() }}</h3>
                <button @click.stop.prevent="preventOverride" v-if="canOverride" class="btn btn-override-disable">
                  Disable Override
                </button>
                <button @click.stop.prevent="allowOverride" v-else class="btn btn-override-allow">
                  Allow Override
                </button>
              </div>

              <div class="table-wrapper">
                <table class="table detailed-table">
                  <thead class="table-header-dark">
                    <tr>
                      <th scope="col">#</th>
                      <th scope="col">Contestant Name</th>
                      <th scope="col">Contestant Agency</th>
                      <th scope="col" v-for="(criteria) in (scoreCards[0]?.judgeScores[0]?.scores || [])"
                        :key="criteria.criteriaId">
                        <div class="criteria-header">
                          <span class="criteria-name">{{ criteria.criterionName }}</span>
                          <small class="criteria-weight">{{ criteria.weight }}%</small>
                        </div>
                      </th>
                      <th scope="col">Total Score</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="scoreCard in scoreCards" :key="scoreCard.contestantId">
                      <td class="contestant-number-highlight">{{ scoreCard.userPK }}</td>
                      <td class="contestant-name-bold">{{ scoreCard.contestantName }}</td>
                      <td class="contestant-agency">{{ scoreCard.contestantAgency }}</td>
                      <td v-for="(score) in (scoreCard?.judgeScores[0]?.scores || [])" :key="score.scoreId"
                        :class="getScoreClass(score.scoreValue)">
                        {{ score.scoreValue }}
                      </td>
                      <td class="total-score-bold">{{ (scoreCard.totalScore ? (scoreCard.totalScore * 10).toFixed(2) :
                        '0.00') }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <div class="signature-section d-print-block d-none">
                <h3 class="signature-line">
                  {{ selectedJudge?.description?.toUpperCase() }}
                </h3>
              </div>
            </div>
          </div>
        </div>

        <!-- Online Users Sidebar -->
        <div class="d-print-none col-lg-3">
          <div class="online-users-panel">
            <h5 class="panel-title">
              <i class="bi bi-people-fill me-2"></i>
              Online Users
              <button class="btn btn-outline-warning btn-sm ms-auto" @click="handleBroadcastToAllClick">📢</button>
            </h5>
            <ul class="users-list">
              <li @click="handleUserClick(user)" v-for="(user, index) in onlineUsers" :key="index" class="user-item">
                <div class="user-info">
                  <i class="bi bi-person-check online-indicator"></i>
                  <span class="user-name">{{ user?.userName }}</span>
                </div>
                <span class="instance-badge">{{ user?.instanceCount }}</span>
              </li>
            </ul>
          </div>
        </div>
      </div>
    </div>

    <BroadcastModal :show-broadcast-modal="showBroadcastModal" :title="broadcastTo" @broadcast="handleBroadcast"
      @close="toggleBroadcastModal" />
  </AdminLayout>

</template>

<script setup>
import BroadcastModal from "@/components/modals/BroadcastModal.vue";
import AdminLayout from "@/layouts/AdminLayout.vue";
import adminService from "@/services/adminService";
import confirmationService from "@/services/confirmationService";
import dashboardService from "@/services/dashboardService";
import signalRService from "@/services/signalRService";
import spinnerService from "@/services/spinnerService";
import toastService from "@/services/toastService";
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";

const showBroadcastModal = ref(false);
const judges = ref([]);
const scoreCards = ref([{ judgeScores: [] }]);
const overAllScoreCards = ref([]);
const selectedJudgeId = ref(null);
const events = ref([]);
const eventId = ref(null);
const resultCanvas = ref(null);
const canvasWidth = 800;
const canvasHeight = 600;
const currentEvent = ref({});
const canOverride = ref(false);
const selectedJudge = ref({});
const onlineUsers = ref([]);
const currentUserId = ref(null);
const broadcastTo = ref(null);
//toggle Broadcast Modal
const toggleBroadcastModal = () => {
  showBroadcastModal.value = !showBroadcastModal.value;
}

const handleBroadcastToAllClick = () => {
  currentUserId.value = null;
  broadcastTo.value = null;
  toggleBroadcastModal();
}

const addToMagicThreeScores = async () => {
  const confirmed = await confirmationService.show("Are you sure you want to add these scores?");
  if (!confirmed) return;
  const resp = await dashboardService.saveOverAllScoreCards(eventId.value, overAllScoreCards.value);
  if (resp.success) {
    toastService.success("Scores saved successfully!");
  }
  else {
    console.error(resp.message);
    toastService.error(resp.message);
  }
}

const handleBroadcast = async (msg) => {
  spinnerService.show();

  const resp = currentUserId.value === null ? await adminService.broadcastMessage(msg) : await adminService.broadcastMessageToUser(currentUserId.value, msg);
  if (resp.success) {
    toastService.success("Broadcast successfull");
  }
  else
    toastService.error(resp.message);
  spinnerService.hide();
}

const handleUserClick = (user) => {
  currentUserId.value = user.userId;
  broadcastTo.value = `Broadcast message to: ${user.userName}`;
  toggleBroadcastModal();
}

// Fetch initial scores
const fetchData = async () => {
  spinnerService.show();
  if (judges.value.length > 0) {
    let judge = localStorage.getItem("recent-judge");
    if (judge) {
      // Parse the value from localStorage (assuming it's a valid JSON string)
      judge = JSON.parse(judge);
      selectJudge(judge);
    } else {
      let jId = judges.value[0].id;
      localStorage.setItem("recent-judge", JSON.stringify({ id: jId }));
      selectJudge({ id: jId });
    }
  }
  else {
    scoreCards.value = [];
  }
  spinnerService.hide();
};

const setCurrentEventSelection = () => {
  const idx = events.value.findIndex(x => x.id === eventId.value);
  if (idx > -1) {
    currentEvent.value = { ...events.value[idx] }
  }
  else {
    currentEvent.value = {};
  }
}


const setAsActive = async () => {
  const confirmed = await confirmationService.show("Are you sure you want to perform this action?");
  if (!confirmed)
    return;
  spinnerService.show();

  const resp = await dashboardService.setAsActive(eventId.value);
  if (resp.success) {
    toastService.success("Action successful!");
  }
  else {
    toastService.error(resp.message);
  }
  spinnerService.hide();
}

const selectJudge = async (judge) => {
  selectedJudgeId.value = judge.id;
  spinnerService.show();
  const par = { judgeid: judge.id, eventid: eventId.value };
  const response = await dashboardService.getCurrentScoresByParams(par);
  console.log('response', response);
  scoreCards.value = response.scoreCards || [{ judgeScores: [] }];
  selectedJudge.value = response.judge;
  canOverride.value = response.canOverride;
  localStorage.setItem("recent-judge", JSON.stringify(judge));
  spinnerService.hide();
}

const fetchJudges = async () => {
  judges.value = await dashboardService.getCurrentEventJudges(eventId.value);
}

const fetchOverAllScores = async () => {
  overAllScoreCards.value = await dashboardService.getOverAllScoreCardsByEventId(eventId.value);
};
const fetchAllEvents = async () => {
  events.value = await dashboardService.getAllEvents();
};


// Watch for changes in score values to update total score
const calculateTotalScores = () => {
  scoreCards.value.forEach((scoreCard) => {
    const judgeScore = scoreCard.judgeScores[0];
    if (judgeScore) {
      scoreCard.totalScore = judgeScore.scores.reduce((total, score) => {
        return total + score.scoreValue * (score.weight / 100);
      }, 0);
    }
  });
};



const drawResultCard = (winners) => {
  const canvas = resultCanvas.value;
  const ctx = canvas.getContext("2d");

  // Set canvas dimensions for half an A4 page (vertical)
  const canvasWidth = 1240;
  const canvasHeight = 1754;
  canvas.width = canvasWidth;
  canvas.height = canvasHeight;

  const img = new Image();
  img.src = new URL('@/assets/result-card.png', import.meta.url).href;
  return new Promise((resolve) => {
    img.onload = () => {
      // Clear the previous canvas content
      ctx.clearRect(0, 0, canvasWidth, canvasHeight);
      // Draw the result card image onto the canvas
      ctx.drawImage(img, 0, 0, canvasWidth, canvasHeight);

      // Determine font size and spacing based on the number of winners
      const baseFontSize = winners.length > 3 ? 46 : 52; // Smaller font for more winners
      const spacing = winners.length > 3 ? 90 : 120; // Adjust spacing for compact layout
      const lineHeight = 50; // Space between lines for each winner's block
      let yPosition = canvasHeight / 3; // Start from one-third down the canvas
      if (winners.length < 3) {
        yPosition = canvasHeight / 2;
      }

      // Function to draw a rounded rectangle
      function drawRoundedRect(ctx, x, y, width, height, radius) {
        ctx.beginPath();
        ctx.moveTo(x + radius, y);
        ctx.lineTo(x + width - radius, y);
        ctx.quadraticCurveTo(x + width, y, x + width, y + radius);
        ctx.lineTo(x + width, y + height - radius);
        ctx.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
        ctx.lineTo(x + radius, y + height);
        ctx.quadraticCurveTo(x, y + height, x, y + height - radius);
        ctx.lineTo(x, y + radius);
        ctx.quadraticCurveTo(x, y, x + radius, y);
        ctx.closePath();
      }

      winners.forEach((winner) => {
        // Adjust font size dynamically
        const rankFontSize = winner.rank === 1 ? baseFontSize + 8 : baseFontSize;
        const agencyFontSize = baseFontSize - 2;
        const rankTextFontSize = baseFontSize - 4;

        // Measure text dimensions for the background
        ctx.font = `bold ${rankFontSize}px Arial`;
        const text = `#${winner.userPK} - ${winner.contestantName}`;
        const textMetrics = ctx.measureText(text);
        const textWidth = textMetrics.width;
        const textHeight = rankFontSize; // Approximate height based on font size
        const padding = 20; // Padding for the background
        const bgWidth = textWidth + padding * 2;
        const bgHeight = textHeight + padding * 1.5;

        // Draw the rounded background
        const bgX = (canvasWidth - bgWidth) / 2; // Center horizontally
        const bgY = yPosition - bgHeight / 2; // Center vertically with text
        ctx.fillStyle = "rgba(0, 0, 0, 0.7)"; // Semi-transparent black background
        drawRoundedRect(ctx, bgX, bgY - 15, bgWidth, bgHeight, 15); // Radius of 15 for rounded corners
        ctx.fill();

        // Draw user rank and name
        ctx.fillStyle = "white";
        ctx.textAlign = "center";
        ctx.strokeStyle = "black";
        ctx.lineWidth = 2;
        ctx.strokeText(text, canvasWidth / 2, yPosition);
        ctx.fillText(text, canvasWidth / 2, yPosition);

        // Draw agency name below the name
        yPosition += lineHeight; // Move down for agency
        ctx.font = `bold ${agencyFontSize}px Arial`;
        ctx.strokeText(winner.contestantAgency, canvasWidth / 2, yPosition);
        ctx.fillText(winner.contestantAgency, canvasWidth / 2, yPosition);

        // Draw rank label below the agency name
        yPosition += lineHeight; // Move down for rank text
        if (winner.rank === 1) {
          ctx.font = `italic bold underlined ${rankTextFontSize}px Arial`;
          const textWidth = ctx.measureText(getRankText(winner.rank)).width;
          const underlineY = yPosition + 5;
          ctx.beginPath();
          ctx.moveTo((canvasWidth - textWidth) / 2, underlineY);
          ctx.lineTo((canvasWidth + textWidth) / 2, underlineY);
          ctx.lineWidth = 2;
          ctx.strokeStyle = "white";
          ctx.stroke();
        } else {
          ctx.font = `italic ${rankTextFontSize}px Arial`;
        }
        ctx.font = `italic ${rankTextFontSize}px Arial`;
        const rankText = getRankText(winner.rank);
        const rankTextMetrics = ctx.measureText(rankText);
        const rankTextWidth = rankTextMetrics.width;
        const rankTextHeight = rankTextFontSize; // Approximate height based on font size
        const rankPadding = 15; // Padding for the background
        const rankBgWidth = rankTextWidth + rankPadding * 2;
        const rankBgHeight = rankTextHeight + rankPadding * 1.5;

        // Draw the rounded background for rank text
        const rankBgX = (canvasWidth - rankBgWidth) / 2; // Center horizontally
        const rankBgY = yPosition - rankBgHeight / 2; // Center vertically with text
        ctx.fillStyle = "rgba(0, 0, 0, 0.7)"; // Semi-transparent black background
        drawRoundedRect(ctx, rankBgX, rankBgY - 15, rankBgWidth, rankBgHeight, 10); // Radius of 10 for rounded corners
        ctx.fill();

        // Draw the rank text
        ctx.fillStyle = "gold";
        ctx.strokeText(rankText, canvasWidth / 2, yPosition);
        ctx.fillText(rankText, canvasWidth / 2, yPosition);

        // Adjust Y-position for the next winner block
        yPosition += spacing; // Add spacing between blocks
      });

      // const copyright = "© 2024 Exodous I.T. Solutions. All Rights Reserved.";
      // ctx.fillStyle = "white";
      // ctx.font = `bold 40px Arial`;
      // ctx.strokeText(copyright, canvasWidth / 2, 1700);
      // ctx.fillText(copyright, canvasWidth / 2, 1700);

      // Resolve the promise with the canvas data URL (PNG image)
      resolve(canvas.toDataURL("image/png"));
    };
  });
};


// Helper function to handle ranking text display (for ties)
const getRankText = (rank) => {
  if (currentEvent.value.name === "MAGIC 3") {
    return "MAGIC 3";
  }
  const idx = events.value.findIndex(x => x.id === eventId.value);
  switch (rank) {
    case 1:
      return events.value[idx].name;
    case 2:
      return "1st Runner Up"; // Rank 2 gets "1st Runner Up"
    case 3:
      return "2nd Runner Up"; // Rank 3 gets "2nd Runner Up"
    default:
      return `${rank}th Runner Up`; // For other ranks, just display the rank number followed by 'th'
  }
};

// Compute tied ranks
const tiedRanks = computed(() => {
  const rankCount = {};
  overAllScoreCards.value.forEach((card) => {
    rankCount[card.rank] = (rankCount[card.rank] || 0) + 1;
  });

  return new Set(Object.keys(rankCount).filter((rank) => rankCount[rank] > 1).map(Number));
});

// Check if a rank is tied
const isTied = (rank) => {
  return tiedRanks.value.has(rank);
};


const printWinners = async () => {
  const idx = events.value.findIndex(x => x.id === eventId.value);
  const evnt = events.value[idx];

  // Extract the number of winners from the event
  const numberOfWinners = evnt.numberWinners; // Adjust this based on your event data structure

  // Filter candidates whose rank is less than or equal to the number of winners
  const eligibleWinners = overAllScoreCards.value.filter(candidate => candidate.rank <= numberOfWinners);

  // Sort the eligible candidates by their rank
  eligibleWinners.sort((a, b) => a.rank - b.rank);

  // Get the canvas image data URL by drawing the result card
  const resultCardImage = await drawResultCard(eligibleWinners);

  // Create a temporary printable element to display the image
  const printableDiv = document.createElement("div");
  printableDiv.style.position = "fixed";
  printableDiv.style.top = "0";
  printableDiv.style.left = "0";
  printableDiv.style.width = "100%";
  printableDiv.style.height = "100%";
  printableDiv.style.background = "white";
  printableDiv.style.display = "flex";
  printableDiv.style.justifyContent = "center";
  printableDiv.style.alignItems = "center";
  printableDiv.style.zIndex = "10000";

  // Add an image to the printable element
  const img = document.createElement("img");
  img.src = resultCardImage;
  img.style.maxWidth = "100%";
  img.style.height = "auto";
  printableDiv.appendChild(img);

  // Add the printable element to the DOM
  document.body.appendChild(printableDiv);
  spinnerService.show();
  // Trigger the print dialog
  setTimeout(() => {
    spinnerService.hide();
    window.print();
    // Set a timeout to ensure the print dialog finishes before cleanup
    setTimeout(() => {
      // Remove the printable div after print dialog is closed or canceled
      document.body.removeChild(printableDiv);
    }, 10); // Delay to give time for print dialog to trigger and close
  }, 100);



};

const refreshDashboard = async () => {
  await fetchOverAllScores();
  await fetchJudges();
  await fetchData();
}

const allowOverride = async () => {
  const confirmed = await confirmationService.show("Are you sure you want to allow overriding the score this judge?");
  if (!confirmed) return;
  const response = await dashboardService.allowOverride(eventId.value, selectedJudgeId.value);
  if (response.success) {
    canOverride.value = true;
    toastService.success("Action successful!");
  }
  else {
    toastService.error(response.message);
  }
}

const preventOverride = async () => {
  const confirmed = await confirmationService.show("Are you sure you want to prevent overriding the score this judge?");
  if (!confirmed) return;
  const response = await dashboardService.preventOverride(eventId.value, selectedJudgeId.value);
  if (response.success) {
    canOverride.value = false;
    toastService.success("Action successful!");
  }
  else {
    toastService.error(response.message);
  }
}

const getUserList = async () => {
  if (!signalRService.connection) {
    setTimeout(() => {
      getUserList();
    }, 500);
  }
  else {
    setTimeout(async () => {
      const users = await signalRService.connection.invoke("GetOnlineUsers");
      renderUserList(users);
    }, 1000);

  }

}

// Recalculate total score whenever scores change
watch(scoreCards, calculateTotalScores, { deep: true });

onMounted(async () => {
  signalRService.subscribeToEvent("OnScoreSubmitted", refreshDashboard);
  signalRService.subscribeToEvent("UpdateUserList", handleOnlineUsers);
  getUserList();
  await fetchAllEvents();
  intitalizeEventSelection();
  //refreshDashboard();
});

const handleOnlineUsers = async () => {
  const users = await signalRService.connection.invoke("GetOnlineUsers");
  renderUserList(users);
}

const renderUserList = (userList) => {
  onlineUsers.value = userList || [];
}

const toggleStatus = async () => {
  const confirmed = await confirmationService.show("Are you sure you want to continue?");
  if (!confirmed) return;
  spinnerService.show();
  const response = await dashboardService.toggleEventStatus(eventId.value);
  if (response.success) {
    const idx = events.value.findIndex(x => x.id === eventId.value);
    if (idx > -1) {
      events.value[idx].isOpen = response.ref === "1";
      currentEvent.value.isOpen = response.ref === "1";
      toastService.success("Action successful!");
    }
  }
  else {
    toastService.error(response.message);
  }
  spinnerService.hide();
}

const intitalizeEventSelection = () => {
  const savedEventId = localStorage.getItem("selectedEventId");
  if (savedEventId) {
    eventId.value = savedEventId; // Set the eventId to the saved value
  } else {
    // Set a default value if no saved ID exists
    eventId.value = events.value[0].id;
  }
  refreshDashboard();
}
const saveSelectedEventId = () => {
  if (!eventId.value || eventId.value === "")
    return;
  localStorage.setItem("selectedEventId", eventId.value);
  setCurrentEventSelection();
  intitalizeEventSelection();
};
watch(eventId, saveSelectedEventId, { deep: true })


const getRankClass = (rank) => {
  if (rank === 1) return 'rank-1';
  if (rank === 2) return 'rank-2';
  if (rank === 3) return 'rank-3';
  return '';
}

const getScoreClass = (scoreValue) => {
  return scoreValue <= 5 ? 'score-low' : 'score-normal';
}


onBeforeUnmount(() => {
  signalRService.connection.off("OnScoreSubmitted", refreshDashboard);
  signalRService.connection.off("UpdateUserList", renderUserList);
});

</script>

<style scoped>
.dashboard-container {
  background: linear-gradient(135deg, var(--light-red) 0%, #ffffff 100%);
  min-height: 100vh;
  padding: 20px;
}

/* Control Panel */
.control-panel {
  background: var(--gold-dark);
  padding: 24px;
  border-radius: 16px;
  box-shadow: 0 4px 6px -1px rgba(220, 38, 38, 0.1), 0 2px 4px -1px rgba(220, 38, 38, 0.06);
  border: 1px solid var(--border-light);
}

.controls-row {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

@media (min-width: 576px) {
  .controls-row {
    flex-direction: row;
    align-items: center;
  }
}

.event-select {
  border: 2px solid var(--border-light);
  border-radius: 12px;
  padding: 12px 16px;
  font-weight: 500;
  transition: all 0.3s ease;
  flex: 1;
}

.event-select:focus {
  border-color: var(--primary-red);
  box-shadow: 0 0 0 3px rgba(220, 38, 38, 0.1);
}

.action-buttons {
  display: flex;
  gap: 8px;
}

.btn-active {
  background: linear-gradient(135deg, var(--primary-red), var(--dark-red));
  color: rgb(51, 49, 49);
  border: none;
  border-radius: 12px;
  padding: 12px 20px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-active:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 15px rgba(220, 38, 38, 0.3);
}

.status-btn {
  border-radius: 12px;
  padding: 12px 20px;
  font-weight: 600;
  transition: all 0.3s ease;
  border: 2px solid transparent;
}

.btn-open {
  background: var(--light-red);
  color: var(--primary-red);
  border-color: var(--primary-red);
}

.btn-closed {
  background: var(--primary-red);
  color: rgb(248, 97, 70);
}

.status-btn:hover {
  transform: translateY(-2px);
}

/* Tables Section */
.table-section {
  background: white;
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-light);
}

.table-header {
  display: flex;
  justify-content: flex-end;
  margin-bottom: 20px;
}

.btn-print {
  background: var(--dark-bg);
  color: rgb(248, 196, 23);
  border: none;
  border-radius: 12px;
  padding: 12px 24px;
  font-weight: 600;
  transition: all 0.3s ease;
}

.btn-print:hover {
  background: var(--darker-bg);
  transform: translateY(-2px);
}

/* Tables */
.table-wrapper {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--border-light);
}

.scores-table {
  margin: 0;
  background: rgb(185, 162, 162);
}

.scores-table thead {
  background: linear-gradient(135deg, var(--primary-red), var(--dark-red));
}

.scores-table thead th {
  color: rgb(248, 196, 23);
  font-weight: 600;
  padding: 16px 12px;
  border: none;
  text-align: center;
}

.scores-table tbody tr {
  transition: all 0.3s ease;
}

.scores-table tbody tr:hover {
  background: var(--light-red);
}

.scores-table tbody td {
  padding: 14px 12px;
  border-color: var(--border-light);
  text-align: center;
}

.contestant-number {
  font-weight: 700;
  color: var(--dark-bg);
}

.contestant-name {
  font-weight: 600;
  color: var(--text-dark);
  text-align: left;
}

.contestant-agency {
  text-align: left;
  color: rgb(41, 33, 6);
}

.total-score {
  font-weight: 700;
  color: var(--primary-red);
}

.rank {
  font-weight: 700;
  font-size: 1.1em;
}

.tied-row {
  background: #fef3c7 !important;
  color: #92400e;
}

.tied-row td {
  border-color: rgb(248, 196, 23);
}

/* Judges Section */
.judges-section {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-light);
}

.judges-tabs {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  padding: 0;
  margin-bottom: 24px;
  list-style: none;
}

.judge-tab {
  margin: 0;
}

.tab-button {
  background: var(--light-red);
  border: 2px solid transparent;
  border-radius: 12px;
  padding: 12px 20px;
  color: #070e11;
  font-weight: 500;
  transition: all 0.3s ease;
  cursor: pointer;
}

.tab-button:hover {
  background: #fecaca;
  transform: translateY(-2px);
}

.tab-button.active {
  background: linear-gradient(135deg, var(--primary-red), var(--dark-red));
  color: rgb(248, 196, 23);
  border-color: var(--primary-red);
  box-shadow: 0 4px 12px rgba(220, 38, 38, 0.3);
}

/* Detailed Scores */
.detailed-scores {
  background: var(--light-red);
  border-radius: 12px;
  padding: 20px;
}

.score-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.judge-title {
  color: #070e11;
  font-weight: 700;
  margin: 0;
}

.btn-override-disable {
  background: var(--primary-red);
  color: rgb(129, 112, 112);
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
}

.btn-override-allow {
  background: #6b7280;
  color: rgb(104, 38, 38);
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  font-weight: 600;
}

.detailed-table {
  background: white;
  border-radius: 8px;
  overflow: hidden;
}

.table-header-dark {
  background: var(--dark-bg);
}

.table-header-dark th {
  color: rgb(248, 196, 23);
  font-weight: 600;
  padding: 16px 8px;
  text-align: center;
  border: none;
}

.criteria-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.criteria-name {
  font-size: 0.9em;
}

.criteria-weight {
  font-size: 0.8em;
  opacity: 0.8;
}

.detailed-table tbody tr {
  transition: background-color 0.3s ease;
}

.detailed-table tbody tr:hover {
  background: #f7cccc;
}

.detailed-table tbody td {
  padding: 12px 8px;
  text-align: center;
  border-color: var(--border-light);
  font-weight: 600;
}

.contestant-number-highlight {
  color: #f59e0b;
  font-weight: 700;
  font-size: 1.1em;
}

.contestant-name-bold {
  font-weight: 700;
  text-align: left;
  color: var(--text-dark);
}

.total-score-bold {
  font-weight: 700;
  color: var(--primary-red);
  font-size: 1.1em;
}

/* Score Color Classes */
.score-low {
  background: #fee2e2;
  color: #dc2626;
  font-weight: 700;
}

.score-normal {
  background: transparent;
  color: var(--gold-dark);
  font-weight: 600;
}

/* Rank Color Classes */
.rank-1 {
  color: #f59e0b;
  font-size: 1.2em;
}

.rank-2 {
  color: #6b7280;
  font-size: 1.1em;
}

.rank-3 {
  color: #92400e;
  font-size: 1.1em;
}

/* Online Users Panel */
.online-users-panel {
  background: white;
  border-radius: 16px;
  padding: 24px;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  border: 1px solid var(--border-light);
  height: fit-content;
  position: sticky;
  top: 20px;
}

.panel-title {
  color: #443d18;
  font-weight: 700;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
}

.users-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.user-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: var(--light-red);
  border-radius: 8px;
  transition: all 0.3s ease;
  cursor: pointer;
}

.user-item:hover {
  background: #fecaca;
  transform: translateX(4px);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.online-indicator {
  color: #10b981;
  font-size: 1.1em;
}

.user-name {
  font-weight: 600;
  color: rgb(190, 165, 111);
}

.instance-badge {
  background: var(--primary-red);
  color: rgb(190, 165, 111);
  border-radius: 12px;
  padding: 4px 8px;
  font-size: 0.8em;
  font-weight: 700;
  min-width: 24px;
  text-align: center;
}

/* Signature Section */
.signature-line {
  border-top: 2px solid var(--dark-bg);
  padding-top: 12px;
  margin-top: 40px;
  text-align: right;
  color: var(--dark-bg);
  font-weight: 600;
}

/* Print Styles */
.print-header {
  color: var(--dark-bg);
  margin-bottom: 30px;
}

@media print {
  .d-print-none {
    display: none !important;
  }

  .dashboard-container {
    background: white !important;
    padding: 0 !important;
  }
}
</style>